/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/require-await */
import {
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Text,
  TextInput
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useIntegrationApp } from "@membranehq/react";
import { toast } from "sonner";
import { IconInfoCircle, IconPlus, IconRefresh, IconTrash, IconWorld } from "@tabler/icons-react";
import { Check, ChevronDown, Search } from "lucide-react";
import { useEffect, useState, useMemo, useRef, memo, forwardRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useCreateBulkFields, useFieldMappings, useCreateBulkFieldMappings, useUpdateFieldMapping, useDeleteFieldMapping } from "../../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import HeIcon from "../../../../../assets/images/heSm.svg?react";
import HubspotIcon from "../../../../../assets/images/hubspot.svg?react";
import SegmentIcon from "../../../../../assets/images/segmentAvatar.svg?react";
import SlackIcon from "../../../../../assets/images/slack.svg?react";
import SalesforceIcon from "../../../../../assets/images/salesforce.svg?react";
import { Alert as DsAlert, AlertDescription } from "../../../../../components/ui/alert";
import { Button as DsButton } from "../../../../../components/ui/button";
import { Checkbox as DsCheckbox } from "../../../../../components/ui/checkbox";
import { Input as DsInput } from "../../../../../components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../../components/ui/dropdown-menu";
import type { CreateFieldRequest, DraftFieldMapping } from "../types";
import type { FieldDefinition, HubSpotField } from "../../../../../services/syncConfiguration/syncConfigurationEndpoints.interface";

interface FieldMappingTableProps {
  slug: string;
  mappings: DraftFieldMapping[];
  onChange: (mappings: DraftFieldMapping[]) => void;
  isConnected: boolean;
  workspaceFields?: FieldDefinition[];
  hubspotFields?: HubSpotField[];
  workspaceObjectId?: string;
  syncId?: string;
  connectionId?: string;
  onRefreshFields?: () => void;
  roleMappings?: any[]; // Role mappings from parent
  matcherField?: { hubspot: string; hyperengage: string }; // Matcher field configuration
  onRoleMappingsExtracted?: (roleMappings: any[]) => void; // Callback to pass extracted role mappings to parent
  onRelationshipMappingsExtracted?: (relationshipMappings: any[]) => void; // Callback to pass extracted relationship mappings to parent
  onExistingMappingIdsExtracted?: (mappingIds: Record<string, string>) => void; // Callback to pass existing mapping IDs to parent
  providerLabel?: string;
  providerKey?: string;
  hideHeader?: boolean;
  persistenceMode?: "component" | "managed";
  reservedProviderFields?: Set<string>;
}

interface CreateFieldModalProps {
  opened: boolean;
  slug: string;
  onClose: () => void;
  onSubmit: (field: CreateFieldRequest) => void;
  hubspotField?: HubSpotField;
  providerLabel?: string;
}

const CreateFieldModal = ({ opened, onClose, slug, onSubmit, hubspotField, providerLabel = 'Provider' }: CreateFieldModalProps) => {
  const [formData, setFormData] = useState<CreateFieldRequest>({
    field_name: "",
    display_name: "",
    field_type: "string",
    is_required: false,
    show_in_profiles: true,
    description: "",
  });

  useEffect(() => {
    if (hubspotField && opened) {
      const mapHubSpotTypeToHyperengage = (type: string, format?: string): CreateFieldRequest['field_type'] => {
        const lowerType = type?.toLowerCase();
        const lowerFormat = format?.toLowerCase();

        // Special case: HubSpot date fields often have type:"string" + format:"date-time"
        if (lowerType === 'string' && lowerFormat === 'date-time') {
          return 'datetime';
        }

        switch (lowerType) {
          case 'string':
          case 'enumeration':
            return 'string';
          case 'number':
            return 'float';
          case 'integer':
            return 'int';
          case 'boolean':
            return 'boolean';
          case 'datetime':
          case 'date':
            return 'datetime';
          case 'object':
          case 'array':
            return 'list';
          default:
            return 'string';
        }
      };

      const fieldName = hubspotField.key.toLowerCase().replace(/[^a-z0-9]/g, '_');
      setFormData({
        field_name: fieldName,
        display_name: hubspotField.title || hubspotField.key,
        field_type: mapHubSpotTypeToHyperengage(hubspotField.type, (hubspotField as any).format),
        is_required: false,
        show_in_profiles: true,
        description: `Synced from ${providerLabel}: ${hubspotField.title || hubspotField.key}`,
      });
    }
  }, [hubspotField, opened]);

  const handleSubmit = () => {
    onSubmit(formData);
    onClose();
    setFormData({
      field_name: "",
      display_name: "",
      field_type: "string",
      is_required: false,
      show_in_profiles: true,
      description: "",
    });
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Create New Field" size="md">
      <Stack gap="sm">
        <TextInput
          label="Field Name"
          placeholder="company_name"
          value={formData.field_name}
          onChange={(e) => setFormData({ ...formData, field_name: e.currentTarget.value })}
          required
          description="Internal field name (lowercase, underscores only)"
        />

        <TextInput
          label="Display Name"
          placeholder="Company Name"
          value={formData.display_name}
          onChange={(e) => setFormData({ ...formData, display_name: e.currentTarget.value })}
          required
          description="Human-readable field name"
        />

        <Select
          label="Field Type"
          data={[
            { value: "string", label: "String" },
            { value: "int", label: "Integer" },
            { value: "float", label: "Float" },
            { value: "boolean", label: "Boolean" },
            { value: "datetime", label: "Date/Time" },
            { value: "list", label: "List/Array" },
          ]}
          value={formData.field_type}
          onChange={(value) => setFormData({ ...formData, field_type: value as any })}
        />

        <TextInput
          label="Description"
          placeholder="Field description..."
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.currentTarget.value })}
        />

        <Group>
          <LabeledCheckbox
            label="Required field"
            checked={formData.is_required}
            onChange={(checked) => setFormData({ ...formData, is_required: checked })}
          />
          <LabeledCheckbox
            label={`Show in ${slug === 'contacts' ? 'contact' : slug === 'accounts' ? 'account' : slug === 'companies' ? 'company' : slug} profiles`}
            checked={formData.show_in_profiles}
            onChange={(checked) => setFormData({ ...formData, show_in_profiles: checked })}
          />
        </Group>

        <DsAlert className="border-blue-200 bg-blue-50 px-3 py-2">
          <IconInfoCircle size="1rem" className="h-4 w-4 text-blue-700" />
          <AlertDescription className="text-xs text-blue-900">
            This field will be available for all {slug === 'contacts' ? 'contacts' : slug === 'accounts' ? 'accounts' : slug === 'companies' ? 'companies' : slug} in Hyperengage
          </AlertDescription>
        </DsAlert>

        <Group justify="flex-end">
          <DsButton type="button" variant="ghost" size="sm" className="h-8 text-[12px]" onClick={onClose}>
            Cancel
          </DsButton>
          <DsButton
            type="button"
            size="sm"
            className="h-8 text-[12px]"
            onClick={handleSubmit}
            disabled={!formData.field_name || !formData.display_name}
          >
            Create Field
          </DsButton>
        </Group>
      </Stack>
    </Modal>
  );
};

interface FieldSelectOptionProps extends React.ComponentPropsWithoutRef<'div'> {
  label: string;
  source?: string;
}

const getFieldSourceIcon = (source?: string) => {
  const normalized = (source || 'native').toLowerCase();

  switch (normalized) {
    case 'native':
    case 'hyperengage':
      return <HeIcon className="!w-3 !h-3 " />;
    case 'hubspot':
      return <HubspotIcon className="w-3 h-3" aria-hidden />;
    case 'segment':
      return <SegmentIcon className="w-3 h-3" aria-hidden />;
    case 'slack':
      return <SlackIcon className="w-3 h-3" aria-hidden />;
    case 'salesforce':
      return <SalesforceIcon className="w-3 h-3" aria-hidden />;
    default:
      return <HeIcon className="!w-3 !h-3" />;
  }
};

const FieldSourceIcon = ({ source }: { source?: string }) => (
  <span className="flex items-center justify-center" style={{ width: 16 }}>
    {getFieldSourceIcon(source)}
  </span>
);

const FieldSelectOption = forwardRef<HTMLDivElement, FieldSelectOptionProps>(
  ({ label, source, ...others }, ref) => (
    <div ref={ref} {...others}>
      <Group gap={6} align="center">
        <FieldSourceIcon source={source || 'native'} />
        <Text size="xs" truncate>
          {label}
        </Text>
      </Group>
    </div>
  ),
);

FieldSelectOption.displayName = 'FieldSelectOption';

const renderFieldLabelWithIcon = (label: string, source?: string) => (
  <Group gap={6} align="center">
    <FieldSourceIcon source={source} />
    <Text size="xs" fw={500} c="dark">
      {label}
    </Text>
  </Group>
);

interface CreateFieldOptionProps extends React.ComponentPropsWithoutRef<'div'> {
  label: string;
}

interface LabeledCheckboxProps {
  checked: boolean;
  label: string;
  description?: string;
  onChange: (checked: boolean) => void;
}

const LabeledCheckbox = ({ checked, label, description, onChange }: LabeledCheckboxProps) => (
  <label className="flex items-start gap-2 cursor-pointer">
    <DsCheckbox
      checked={checked}
      onCheckedChange={(value) => onChange(value === true)}
      className="mt-0.5 h-4 w-4"
    />
    <span>
      <Text size="xs" fw={500}>{label}</Text>
      {description ? <Text size="xs" c="dimmed">{description}</Text> : null}
    </span>
  </label>
);

const CreateFieldOption = forwardRef<HTMLDivElement, CreateFieldOptionProps>(
  ({ label, ...others }, ref) => (
    <div ref={ref} {...others}>
      <Group gap={6} align="center">
        <Text size="xs">{label}</Text>
      </Group>
    </div>
  ),
);

CreateFieldOption.displayName = 'CreateFieldOption';

type RowSelectOption = {
  value: string;
  label: string;
  searchText?: string;
};

interface SearchableRowSelectProps {
  value: string;
  options: RowSelectOption[];
  placeholder: string;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
  noResultsText?: string;
}

const SearchableRowSelect = ({
  value,
  options,
  placeholder,
  onChange,
  className = "",
  disabled = false,
  noResultsText = "No fields found",
}: SearchableRowSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return options;
    return options.filter((option) => {
      const haystack = `${option.label} ${option.searchText || ""}`.toLowerCase();
      return haystack.includes(search);
    });
  }, [options, query]);

  useEffect(() => {
    if (!isOpen) setQuery("");
  }, [isOpen]);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={`flex items-center justify-between gap-2 text-left ${className} ${!selectedOption ? "text-muted-foreground" : ""}`}
        >
          <span className="truncate">{selectedOption?.label || placeholder}</span>
          <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)] p-0">
        <div className="border-b border-border-chip p-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <DsInput
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              onKeyDown={(event) => event.stopPropagation()}
              placeholder="Search fields..."
              className="h-8 pl-8"
            />
          </div>
        </div>
        <div className="max-h-[260px] overflow-y-auto py-1">
          {filteredOptions.length ? filteredOptions.map((option) => (
            <DropdownMenuItem
              key={option.value || "__empty__"}
              onSelect={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={option.value === value ? "bg-accent-tint text-primary" : undefined}
            >
              <div className="flex w-full items-center gap-2">
                <span className="inline-flex h-4 w-4 items-center justify-center">
                  {option.value === value ? <Check className="h-3.5 w-3.5 text-primary" /> : null}
                </span>
                <span className="truncate">{option.label}</span>
              </div>
            </DropdownMenuItem>
          )) : (
            <div className="px-3 py-2 text-xs text-muted-foreground">{noResultsText}</div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const FieldMappingTable = ({
  slug,
  mappings,
  onChange,
  isConnected,
  workspaceFields = [],
  hubspotFields = [],
  workspaceObjectId,
  syncId,
  connectionId,
  onRefreshFields,
  roleMappings = [],
  matcherField,
  onRoleMappingsExtracted,
  onRelationshipMappingsExtracted,
  onExistingMappingIdsExtracted,
  providerLabel = 'HubSpot',
  providerKey = 'hubspot',
  hideHeader = false,
  persistenceMode = "component",
  reservedProviderFields = new Set()
}: FieldMappingTableProps) => {
  const integrationApp = useIntegrationApp();
  const workspace: any = useWorkspaceStore((s: any) => s.workspace);
  const queryClient = useQueryClient();
  const mappingInputClass = "h-9 w-full rounded-[5px] border border-border-chip bg-background px-3 text-[13px] text-foreground";
  // Handle matcher field changes by removing conflicting mappings
  const matcherCleanupDoneRef = useRef(false);
  // Mutation hooks
  const createBulkFieldMappingsMutation = useCreateBulkFieldMappings();
  const updateFieldMappingMutation = useUpdateFieldMapping();
  const deleteFieldMappingMutation = useDeleteFieldMapping();
  const createBulkFieldsMutation = useCreateBulkFields();
  const [loading, setLoading] = useState(false);
  const [existingMappingIds, setExistingMappingIds] = useState<Record<string, string>>({});
  const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] = useDisclosure(false);
  const [modalHubSpotField, setModalHubSpotField] = useState<HubSpotField | undefined>(undefined);
  const [creatingFieldForMappingIndex, setCreatingFieldForMappingIndex] = useState<number | undefined>(undefined);
  const [savingMappings, setSavingMappings] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [unsavedMappingIds, setUnsavedMappingIds] = useState<Set<string>>(new Set());
  const [existingFieldMappings, setExistingFieldMappings] = useState<any[]>([]);
  const [hydrated, setHydrated] = useState(false);

  // Provider system fields that are automatically mapped for every write_in sync
  const systemPrefix = (providerKey || 'hubspot').toLowerCase();
  const hubspotSystemFields = [`${systemPrefix}_url`, `${systemPrefix}_record_id`];

  const matcherHyperengageFieldDefinition = useMemo(() => (
    matcherField?.hyperengage
      ? workspaceFields.find((field) => field.name === matcherField.hyperengage)
      : undefined
  ), [matcherField?.hyperengage, workspaceFields]);
  // Get all HubSpot fields that are already in use to exclude from field mapping selection
  const getUsedHubSpotFields = (): Set<string> => {
    const usedFields = new Set<string>();

    // Add fields from current field mappings
    mappings.forEach(mapping => {
      if (mapping.source_field_name) {
        usedFields.add(mapping.source_field_name);
      }
    });

    // Add matcher field
    if (matcherField?.hubspot) {
      usedFields.add(matcherField.hubspot);
    }

    // Add role mapping fields (regular field mappings cannot use fields used by role mappings)
    roleMappings.forEach(roleMapping => {
      if (roleMapping.hubspot_owner_field) {
        usedFields.add(roleMapping.hubspot_owner_field);
      }
    });

    return usedFields;
  };

  // Check if HubSpot field type is compatible with Hyperengage field type
  const areTypesCompatible = (hubspotField: HubSpotField, hyperengageField: FieldDefinition): boolean => {
    const hsType = hubspotField.type?.toLowerCase();
    const heType = hyperengageField.data_type?.toLowerCase();
    const hsFormat = (hubspotField as any).format?.toLowerCase(); // Handle format field

    // Map HubSpot types to compatible Hyperengage types
    const typeCompatibility: Record<string, string[]> = {
      string: ['string', 'text'],
      enumeration: ['string', 'text'],
      text: ['string', 'text'],
      number: ['int', 'integer', 'float', 'decimal', 'number'], // HubSpot number can map to both int and float
      integer: ['int', 'integer', 'float', 'decimal', 'number'],
      float: ['int', 'integer', 'float', 'decimal', 'number'],
      decimal: ['int', 'integer', 'float', 'decimal', 'number'],
      boolean: ['boolean'],
      datetime: ['datetime', 'date', 'timestamp'],
      date: ['datetime', 'date', 'timestamp'],
      timestamp: ['datetime', 'date', 'timestamp'],
      object: ['json', 'list', 'array'],
      array: ['json', 'list', 'array']
    };

    if (!hsType || !heType) return true; // Allow if type info is missing

    // Special case: HubSpot date fields often have type:"string" + format:"date-time"
    if (hsType === 'string' && hsFormat === 'date-time') {
      return ['date', 'datetime'].includes(heType);
    }

    const compatibleHeTypes = typeCompatibility[hsType] || [];
    return compatibleHeTypes.includes(heType) || heType === hsType;
  };

  // Get display type for HubSpot field (resolves date fields properly)
  const getHubSpotFieldDisplayType = (field: HubSpotField): string => {
    const type = field.type?.toLowerCase();
    const format = (field as any).format?.toLowerCase();

    // Special case: HubSpot date fields often have type:"string" + format:"date-time"
    if (type === 'string' && format === 'date-time') {
      return 'datetime';
    }

    return field.type || 'unknown';
  };

  // Get compatible Hyperengage fields for a given HubSpot field
  const getCompatibleHyperengageFields = (hubspotField?: HubSpotField, includeMappingField?: FieldDefinition) => {
    // Ensure workspaceFields is always an array
    const safeWorkspaceFields = Array.isArray(workspaceFields) ? workspaceFields : [];

    // Add the mapping's field if it's not in workspaceFields (e.g., newly created)
    const allFields = includeMappingField && !safeWorkspaceFields.find(f => f.id === includeMappingField.id)
      ? [...safeWorkspaceFields, includeMappingField]
      : safeWorkspaceFields;

    return allFields.filter(field => {
      if (!field || field.is_protected) return false;
      // Exclude metric fields from write-in sync (they should only be populated from HubSpot, not sent to it)
      if (field.attribute_type === 'metric_traits') return false;
      if (!hubspotField) return true; // Show all fields if no HubSpot field selected
      return areTypesCompatible(hubspotField, field);
    });
  };

  const shouldLoadServerMappings = !!workspace?.id && !!syncId;

  // Use React Query hook for field mappings
  const {
    data: fieldMappingsData,
    isLoading: fieldMappingsLoading,
    isFetching: fieldMappingsFetching,
  } = useFieldMappings({
    workspaceId: workspace?.id,
    syncId: syncId || "",
    enabled: shouldLoadServerMappings,
  });
  const isFieldMappingsHydrationLoading = shouldLoadServerMappings
    && (fieldMappingsLoading || fieldMappingsFetching);

  // Load existing field mappings from database and extract role mappings
  const loadExistingFieldMappings = async () => {
    if (!workspace?.id || !syncId) {
      setHydrated(true);
      setLoading(false);
      return;
    }

    try {
      // On first hydration show blocking loader; afterwards, hydrate silently
      if (!hydrated) setLoading(true);
      const existingMappings = Array.isArray(fieldMappingsData) ? fieldMappingsData : [];

      // Store existing mappings for reference in save operations
      setExistingFieldMappings(existingMappings);

      // Create lookup maps to avoid expensive find() operations
      const workspaceFieldsById = new Map(workspaceFields.map(f => [f.id, f]));
      const workspaceFieldsByName = new Map(workspaceFields.map(f => [f.name, f]));
      const hubspotFieldsByKey = new Map(hubspotFields.map(f => [f.key, f]));
      const roleFieldNames = new Set(['csm_id', 'owner_id', 'ae_id']);
      const systemFieldsSet = new Set(hubspotSystemFields);

      // Process mappings efficiently
      const regularMappings: DraftFieldMapping[] = [];
      const extractedRoleMappings: any[] = [];
      const extractedRelationshipMappings: any[] = [];
      const newExistingMappingIds: Record<string, string> = {};

      existingMappings.forEach((mapping: any) => {
        // Look up hyperengage field efficiently
        let hyperengageField = workspaceFieldsById.get(mapping.destination_field_id);
        if (!hyperengageField && mapping.destination_metric_name) {
          hyperengageField = workspaceFieldsByName.get(mapping.destination_metric_name);
        }

        // Track existing mapping ID for updates (only for regular field mappings)
        if (mapping.id && mapping.source_field_name && !mapping.is_owner_assignment) {
          newExistingMappingIds[mapping.source_field_name] = mapping.id;
        }

        // Skip matcher field
        if (matcherField?.hubspot === mapping.source_field_name &&
          matcherField?.hyperengage === hyperengageField?.name) {
          return; // Skip matcher field
        }

        // Skip system fields
        if (systemFieldsSet.has(mapping.source_field_name)) {
          return; // Skip system fields
        }

        if (mapping.is_owner_assignment) {
          // Role mapping
          if (hyperengageField?.name && roleFieldNames.has(hyperengageField.name)) {
            extractedRoleMappings.push({
              hyperengage_role: hyperengageField.name,
              hubspot_owner_field: mapping.source_field_name,
              display_name: hyperengageField.display_name || hyperengageField.name
            });
          }
        } else if (mapping.is_relationship_assignment || mapping.destination_field_type === 'relation') {
          // Relationship mapping
          if (hyperengageField) {
            const targetObject = mapping.transform_rules?.target_object || 'accounts';
            extractedRelationshipMappings.push({
              hubspot_list_field: mapping.source_field_name,
              display_name: `${targetObject.charAt(0).toUpperCase() + targetObject.slice(1)} Relationships`,
              target_object: targetObject
            });
          }
        } else {
          // Regular field mapping
          regularMappings.push({
            source_field_name: mapping.source_field_name,
            destination_field_id: mapping.destination_field_id,
            destination_field_type: mapping.destination_field_type || "field",
            is_required: mapping.is_required || false,
            mapping_status: "mapped" as const,
            hubspot_field: hubspotFieldsByKey.get(mapping.source_field_name),
            hyperengage_field: hyperengageField,
            metric_aggregation_type: mapping.metric_aggregation_type,
            is_owner_assignment: false,
            transform_rules: mapping.transform_rules || {}
          });
        }
      });

      // Only update mappings if they've actually changed to avoid unnecessary re-renders
      const hasChanges = regularMappings.length !== mappings.length ||
        regularMappings.some((newMapping, index) => {
          const existingMapping = mappings[index];
          return !existingMapping ||
            newMapping.source_field_name !== existingMapping.source_field_name ||
            newMapping.destination_field_id !== existingMapping.destination_field_id ||
            newMapping.mapping_status !== existingMapping.mapping_status ||
            !newMapping.hubspot_field !== !existingMapping.hubspot_field ||
            !newMapping.hyperengage_field !== !existingMapping.hyperengage_field;
        });

      if (hasChanges) {
        onChange(regularMappings);
      }

      // Set existing mapping IDs for update logic
      setExistingMappingIds(newExistingMappingIds);

      if (onRoleMappingsExtracted) {
        // Only override existing role mappings if we extracted some OR if there are none currently
        if (extractedRoleMappings.length > 0 || roleMappings.length === 0) {
          onRoleMappingsExtracted(extractedRoleMappings);
        }
      }

      // Pass extracted relationship mappings back to parent component
      if (onRelationshipMappingsExtracted) {
        // Always pass relationship mappings if we found any
        if (extractedRelationshipMappings.length > 0) {
          onRelationshipMappingsExtracted(extractedRelationshipMappings);
        }
      }

      // Pass existing mapping IDs back to parent component for role mappings
      if (onExistingMappingIdsExtracted) {
        onExistingMappingIdsExtracted(newExistingMappingIds);
      }
    } catch (error) {
      console.error('Failed to load existing field mappings:', error);
    } finally {
      setHydrated(true);
      setLoading(false);
    }
  };

  const fieldMappingsSignature = useMemo(() => {
    if (!Array.isArray(fieldMappingsData)) return "__no_mappings_payload__";
    return fieldMappingsData
      .map((mapping: any) => [
        mapping?.id || "",
        mapping?.source_field_name || "",
        mapping?.destination_field_id || "",
        mapping?.destination_metric_name || "",
        mapping?.is_owner_assignment ? "1" : "0",
        mapping?.is_relationship_assignment ? "1" : "0",
        mapping?.updated_at || "",
      ].join(":"))
      .sort()
      .join("|");
  }, [fieldMappingsData]);
  const hubspotFieldKeysSignature = useMemo(
    () => hubspotFields.map((field) => field.key).sort().join(","),
    [hubspotFields],
  );
  const workspaceFieldIdsSignature = useMemo(
    () => workspaceFields.map((field) => field.id).sort().join(","),
    [workspaceFields],
  );

  // Process field mappings data when relevant source data changes
  useEffect(() => {
    if (hubspotFields.length === 0) return;

    // In managed mode, sync may not exist until first save. Don't block UI on hydration.
    if (!shouldLoadServerMappings) {
      if (!hydrated) setHydrated(true);
      return;
    }

    if (isFieldMappingsHydrationLoading) return;

    loadExistingFieldMappings().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldLoadServerMappings,
    isFieldMappingsHydrationLoading,
    fieldMappingsSignature,
    hubspotFieldKeysSignature,
    workspaceFieldIdsSignature,
    hubspotFields.length,
  ]);

  // Handle matcher field changes by removing conflicting mappings
  useEffect(() => {
    if (!matcherField?.hubspot || !matcherField?.hyperengage) return;

    // Remove any existing field mappings that conflict with the new matcher field
    const updatedMappings = mappings.filter(mapping => {
      // Remove mappings that use the same HubSpot field as the matcher
      const conflictsWithHubSpot = mapping.source_field_name === matcherField.hubspot;

      // Remove mappings that use the same Hyperengage field as the matcher
      const conflictsWithHyperengage = mapping.hyperengage_field?.name === matcherField.hyperengage;

      // Keep mappings that don't conflict
      return !conflictsWithHubSpot && !conflictsWithHyperengage;
    });

    // Only update if there were conflicts to resolve
    if (updatedMappings.length !== mappings.length) {
      onChange(updatedMappings);

      // Show notification only after the first automatic cleanup
      if (matcherCleanupDoneRef.current) {
        const removedCount = mappings.length - updatedMappings.length;
        toast.info('Matcher field updated', {
          description: `Removed ${removedCount} conflicting field mapping${removedCount > 1 ? 's' : ''} due to matcher field change`,
        });
      }
      matcherCleanupDoneRef.current = true;
    }
  }, [matcherField?.hubspot, matcherField?.hyperengage]);

  // Save unified field mappings to database and integration.app
  const saveFieldMappings = async () => {
    if (!workspace?.id || !connectionId) {
      console.error('Missing required IDs for saving field mappings');
      toast.error('Missing configuration', {
        description: 'Connect your integration before saving mappings.',
      });
      return;
    }

    if (!syncId) {
      toast.warning('Create import sync first', {
        description: 'Please create the import sync (Configure Import → Create Import Sync) before saving mappings.',
      });
      return;
    }

    const validMappings = mappings.filter(m =>
      m.source_field_name &&
      m.destination_field_id &&
      m.mapping_status === 'mapped'
    );

    setSavingMappings(true);

    // Show loading notification
    toast.loading('Saving field mappings...', {
      id: 'saving-field-mappings',
      description: 'Updating field mappings in database and integration',
    });

    try {
      // 1. Prepare all mappings (field mappings + matcher field + role mappings)
      const allDbMappings = [];

      // Add regular field mappings
      validMappings.forEach(mapping => {
        const isMetricField = mapping.destination_field_type === 'metric';
        const fieldMappingData: any = {
          source_field_name: mapping.source_field_name,
          source_field_id: mapping.source_field_name,
          destination_field_type: mapping.destination_field_type || "field",
          is_owner_assignment: false,
          transform_rules: mapping.transform_rules || {},
          is_required: mapping.is_required || false
        };

        if (isMetricField) {
          // For metric fields, set destination_metric_name instead of destination_field_id
          const hyperengageField = workspaceFields.find(f => f.id === mapping.destination_field_id);
          fieldMappingData.destination_metric_name = hyperengageField?.name || "";
          // Don't set destination_field_id for metrics
          fieldMappingData.destination_field_id = null;
          // Set metric aggregation type (default to 'value' for write_in)
          fieldMappingData.metric_aggregation_type = mapping.metric_aggregation_type || 'value';
        } else {
          // For regular fields, set destination_field_id
          fieldMappingData.destination_field_id = mapping.destination_field_id;
          // Don't send empty string for destination_metric_name - just omit it
        }

        allDbMappings.push(fieldMappingData);
      });

      // Add matcher field mapping (non-removable)
      if (matcherField?.hubspot && matcherField?.hyperengage) {
        const matcherHyperengageField = workspaceFields.find(f => f.name === matcherField.hyperengage);
        if (matcherHyperengageField) {
          const isMatcherMetric = matcherHyperengageField.attribute_type === 'metric_traits';
          const matcherMappingData: any = {
            source_field_name: matcherField.hubspot,
            source_field_id: matcherField.hubspot,
            destination_field_type: isMatcherMetric ? "metric" : "field",
            is_owner_assignment: false,
            transform_rules: {},
            is_required: true // Matcher field is always required
          };

          if (isMatcherMetric) {
            matcherMappingData.destination_metric_name = matcherHyperengageField.name;
            matcherMappingData.destination_field_id = null;
            matcherMappingData.metric_aggregation_type = 'value';
          } else {
            matcherMappingData.destination_field_id = matcherHyperengageField.id;
          }

          allDbMappings.push(matcherMappingData);
        }
      }

      // Add automatic HubSpot system field mappings (non-editable)
      hubspotSystemFields.forEach(systemFieldName => {
        const hubspotSystemField = workspaceFields.find(f => f.name === systemFieldName);
        if (hubspotSystemField) {
          allDbMappings.push({
            source_field_name: systemFieldName,
            source_field_id: systemFieldName,
            destination_field_id: hubspotSystemField.id,
            destination_field_type: "field" as const,
            is_owner_assignment: false,
            transform_rules: {},
            is_required: true // System fields are always required
          });
        }
      });

      // NOTE: Role mappings are now handled separately by RoleMappingTable
      // They create their own field mappings with is_owner_assignment: true
      // We don't add them here to avoid duplication

      // Save to database - use UPDATE for existing, CREATE for new, DELETE for removed
      const updatePromises: Promise<any>[] = [];
      const deletePromises: Promise<any>[] = [];
      const newMappings: any[] = [];
      const failedUpdateMappings: any[] = []; // Track mappings that failed to update due to 404

      // Track which existing mappings are being kept
      const currentSourceFields = new Set(allDbMappings.map(m => m.source_field_name));

      // Find mappings to delete (exist in backend but not in current state)
      // NOTE: Exclude role mappings (is_owner_assignment=true) from deletion logic
      // as they are managed separately by RoleMappingTable

      Object.entries(existingMappingIds).forEach(([sourceFieldName, mappingId]) => {
        if (!currentSourceFields.has(sourceFieldName)) {
          // Check if this is a role mapping by looking at the original mapping data
          const originalMapping = existingFieldMappings.find((m: any) => m.source_field_name === sourceFieldName);

          if (originalMapping?.is_owner_assignment) {
            // Skip deletion for role mappings - they are managed by RoleMappingTable
            // Skip deletion of role mapping
            return;
          }

          // This mapping was removed, delete it using proper DELETE endpoint
          deletePromises.push(
            deleteFieldMappingMutation.mutateAsync({
              workspace_id: workspace.id,
              sync_id: syncId,
              mapping_id: mappingId
            }).catch((error: any) => {
              // 404 errors are expected when mappings are already deleted, don't log as warnings
              if (error?.response?.status === 404) {
                // Already deleted
              } else {
                console.warn(`Failed to delete field mapping ${mappingId} for field ${sourceFieldName}:`, error);
              }
              // Don't throw - continue with other operations
              return null;
            })
          );
        }
      });

      // Handle updates and creates
      allDbMappings.forEach(mapping => {
        const existingMappingId = existingMappingIds[mapping.source_field_name];

        if (existingMappingId) {
          // Update existing mapping
          updatePromises.push(
            updateFieldMappingMutation.mutateAsync({
              workspace_id: workspace.id,
              sync_id: syncId,
              mapping_id: existingMappingId,
              data: mapping
            }).catch((error: any) => {
              // 404 errors indicate the mapping no longer exists, treat as create instead
              if (error?.response?.status === 404) {
                // Mapping no longer exists, will create a new one
                // Add to failed updates to create later
                failedUpdateMappings.push(mapping);
              } else {
                console.warn(`Failed to update field mapping ${existingMappingId} for field ${mapping.source_field_name}:`, error);
              }
              // Don't throw - continue with other operations
              return null;
            })
          );
        } else {
          // Create new mapping
          newMappings.push(mapping);
        }
      });

      // Execute all operations
      const allPromises = [...updatePromises, ...deletePromises];
      if (allPromises.length > 0) {
        await Promise.all(allPromises);
      }

      // Create new mappings (including those that failed to update due to 404)
      const allNewMappings = [...newMappings, ...failedUpdateMappings];
      if (allNewMappings.length > 0) {
        await createBulkFieldMappingsMutation.mutateAsync({
          workspace_id: workspace.id,
          sync_id: syncId,
          mappings: allNewMappings as any
        });
      }

      // 2. Update integration.app field mapping - REPLACE all importValue (no spreading)
      const fieldMappingKey = slug;

      // Create completely new import value - ONLY current setup's values
      const newImportValue: Record<string, any> = {};

      // Add regular field mappings
      validMappings.forEach(mapping => {
        if (mapping.destination_field_id && mapping.source_field_name) {
          // Use the field name from the mapping's hyperengage_field if available,
          // otherwise look it up in workspaceFields by ID and use name/column_name
          const fieldFromWorkspace = workspaceFields.find(f => f.id === mapping.destination_field_id);
          const fieldColumnName = mapping.hyperengage_field?.name ||
            mapping.hyperengage_field?.column_name ||
            fieldFromWorkspace?.name ||
            fieldFromWorkspace?.column_name;

          // Resolve and use the final field name

          // Only add the mapping if we found a valid field name
          if (fieldColumnName) {
            newImportValue[fieldColumnName] = {
              $var: `$.${mapping.source_field_name}`
            };
          } else {
            console.warn(`Could not find field name for destination_field_id: ${mapping.destination_field_id}`, mapping);
          }
        }
      });

      // Add matcher field mapping
      if (matcherField?.hubspot && matcherField?.hyperengage) {
        newImportValue[matcherField.hyperengage] = {
          $var: `$.${matcherField.hubspot}`
        };
      }

      // Add automatic HubSpot system field mappings to integration.app
      hubspotSystemFields.forEach(systemFieldName => {
        const hubspotSystemField = workspaceFields.find(f => f.name === systemFieldName);
        if (hubspotSystemField) {
          newImportValue[systemFieldName] = {
            $var: `$.${systemFieldName}`
          };
        }
      });

      // NOTE: Role mappings are handled separately by RoleMappingTable
      // We need to preserve existing role mappings when updating integration.app

      // Get current field mapping to preserve existing role mappings
      const currentFieldMapping = await integrationApp
        .connection(connectionId)
        .fieldMapping(fieldMappingKey)
        .get({ autoCreate: true });

      const currentImportValue = currentFieldMapping?.importValue || {};

      // Preserve existing role mappings (identified by $findAppRecordId pattern) and relationship mappings
      const preservedRoleMappings: Record<string, any> = {};
      const preservedRelationshipMappings: Record<string, any> = {};

      Object.entries(currentImportValue).forEach(([key, value]: [string, any]) => {
        if (value?.$findAppRecordId) {
          // This is a role mapping
          preservedRoleMappings[key] = value;
        } else if (key === 'relationships') {
          // This is the special relationships structure for relationship mappings
          // Handle both old $jsonata format and new $iterate object structure
          if (value?.$jsonata || value?.$iterate || typeof value === 'object') {
            preservedRelationshipMappings[key] = value;
          }
        } else {
          // Check if this might be a legacy relationship mapping by looking at workspace fields
          const field = workspaceFields.find((f: any) => f.name === key || f.column_name === key);
          if (field && (
            field.data_type?.toLowerCase().includes('array') ||
            field.data_type?.toLowerCase().includes('list') ||
            field.attribute_type?.toLowerCase().includes('array')
          )) {
            // This looks like it could be a legacy relationship mapping, preserve it
            preservedRelationshipMappings[key] = value;
          }
        }
      });

      // Merge preserved role mappings and relationship mappings with new field mappings
      const finalImportValue = {
        ...newImportValue,
        ...preservedRoleMappings,
        ...preservedRelationshipMappings
      };

      // Update the field mapping in integration.app with merged importValue
      await integrationApp
        .connection(connectionId)
        .fieldMapping(fieldMappingKey)
        .patch({
          importValue: finalImportValue
        });

      setHasUnsavedChanges(false);
      setUnsavedMappingIds(new Set());

      // Show success notification
      toast.success('Field mappings saved!', {
        id: 'saving-field-mappings',
        description: 'Field mappings have been successfully updated',
      });

      if (onRefreshFields) {
        onRefreshFields();
      }
      // Invalidate workspace fields query to refresh field data after saving
      const apiSlugInvalidate = slug === 'contacts' ? 'users' : slug;
      queryClient.invalidateQueries({
        queryKey: ["sync-object-fields", workspace?.id, apiSlugInvalidate]
      });
    } catch (error) {
      console.error('Failed to save unified field mappings:', error);

      // Show error notification
      toast.error('Failed to save field mappings', {
        id: 'saving-field-mappings',
        description: 'An error occurred while saving field mappings. Please try again.',
      });
    } finally {
      setSavingMappings(false);
    }
  };

  const handleFieldMappingChange = (index: number, updates: Partial<DraftFieldMapping>, skipUnsavedTracking = false) => {
    const newMappings = [...mappings];
    newMappings[index] = { ...newMappings[index], ...updates };
    onChange(newMappings);

    if (!skipUnsavedTracking) {
      setHasUnsavedChanges(true);

      // Track this specific mapping as unsaved
      const mappingId = `${newMappings[index].source_field_name || 'new'}-${newMappings[index].destination_field_id || 'unmapped'}-${index}`;
      setUnsavedMappingIds(prev => {
        const newSet = new Set(prev);
        newSet.add(mappingId);
        return newSet;
      });
    }
  };

  const handleAddMapping = () => {
    const newMapping: DraftFieldMapping = {
      source_field_name: "",
      destination_field_id: "",
      destination_field_type: "field",
      is_required: false,
      mapping_status: "unmapped",
    };
    const newMappings = [...mappings, newMapping];
    onChange(newMappings);
    setHasUnsavedChanges(true);

    // Track the new mapping as unsaved
    const mappingId = `new-unmapped-${newMappings.length - 1}`;
    setUnsavedMappingIds(prev => {
      const newSet = new Set(prev);
      newSet.add(mappingId);
      return newSet;
    });
  };

  const handleRemoveMapping = (index: number) => {
    const removedMapping = mappings[index];
    const newMappings = mappings.filter((_, i) => i !== index);
    onChange(newMappings);
    setHasUnsavedChanges(true);

    // Remove the mapping from unsaved tracking
    const mappingId = `${removedMapping.source_field_name || 'new'}-${removedMapping.destination_field_id || 'unmapped'}-${index}`;
    setUnsavedMappingIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(mappingId);
      return newSet;
    });
  };

  const handleCreateField = async (fieldRequest: CreateFieldRequest) => {
    if (!workspace?.id || !workspaceObjectId) {
      console.error('Missing workspace ID or object ID for field creation');
      return;
    }

    // Show loading notification
    toast.loading('Creating field...', {
      id: 'creating-field',
      description: `Creating field "${fieldRequest.display_name}"`,
    });

    try {
      // Map CreateFieldRequest to the API format
      const fieldData = {
        column_name: fieldRequest.field_name,
        display_name: fieldRequest.display_name,
        data_type: fieldRequest.field_type,
        source: providerKey?.toLowerCase() || 'native',
        is_system_field: false,
        is_hidden: false,
        is_protected: false,
        is_updateable: true,
        attribute_type: "object_traits",
        description: fieldRequest.description?.trim()
          ? fieldRequest.description
          : `Synced from ${providerLabel ?? 'Provider'}`,
        default_value: "",
        validation_rules: {},
        display_order: 0,
        object_definition_id: workspaceObjectId,
        workspace_id: workspace.id
      } as any;

      // Create the field using bulk fields API and get the response
      const response = await createBulkFieldsMutation.mutateAsync({
        workspace_id: workspace.id,
        objectId: workspaceObjectId,
        fields: [fieldData],
        objectSlug: slug === 'contacts' ? 'users' : slug
      });

      // Auto-select the newly created field if we were creating it for a specific mapping
      if (creatingFieldForMappingIndex !== undefined && response && response.data && Array.isArray(response.data) && response.data.length > 0) {
        // Use the actual field from the API response
        const createdField = response.data[0];

        // Convert the API response to our FieldDefinition format
        const newField: FieldDefinition = {
          id: createdField.id, // Use the real ID from the response
          column_name: createdField.column_name || createdField.name, // Add required column_name field
          name: createdField.name,
          display_name: createdField.display_name,
          data_type: createdField.data_type,
          attribute_type: createdField.attribute_type,
          is_system_field: createdField.is_system_field,
          is_updateable: createdField.is_updateable,
          is_hidden: createdField.is_hidden,
          is_protected: createdField.is_protected,
          source: createdField.source || providerKey?.toLowerCase() || 'native',
          description: createdField.description,
          default_value: createdField.default_value,
          validation_rules: createdField.validation_rules,
          display_order: createdField.display_order,
          object_definition_id: createdField.object_definition_id,
          workspace_id: createdField.workspace_id,
          created_at: createdField.created_at || new Date().toISOString(),
          updated_at: createdField.updated_at || new Date().toISOString()
        };

        const isMetric = newField.attribute_type === 'metric_traits';

        // Optimistically update workspaceFields cache to include the new field
        // This ensures the field is immediately available for field name lookup
        const apiSlug = slug === 'contacts' ? 'users' : slug;
        queryClient.setQueryData(
          ["sync-object-fields", workspace.id, apiSlug],
          (oldData: any) => {
            if (!oldData?.data) return oldData;
            return {
              ...oldData,
              data: [...oldData.data, newField]
            };
          }
        );

        // Immediately update the mapping with the new field (skip unsaved tracking)
        handleFieldMappingChange(creatingFieldForMappingIndex, {
          destination_field_id: newField.id,
          mapping_status: "mapped",
          destination_field_type: isMetric ? "metric" : "field",
          hyperengage_field: newField,
        }, true); // Skip unsaved tracking since this is an auto-selection

        setCreatingFieldForMappingIndex(undefined);
      }
      // Show success notification
      toast.success('Field created!', {
        id: 'creating-field',
        description: `Successfully created field "${fieldRequest.display_name}"`,
      });
    } catch (error) {
      console.error('Failed to create field:', error);

      // Show error notification
      toast.error('Failed to create field', {
        id: 'creating-field',
        description: 'An error occurred while creating the field. Please try again.',
      });
    } finally {
      // Field creation completed
    }
  };

  if (!isConnected) {
    return (
      <DsAlert className="border-yellow-300 bg-yellow-50 px-4 py-3">
        <AlertDescription className="text-xs text-yellow-900">
          Connect to {providerLabel} first to configure field mappings
        </AlertDescription>
      </DsAlert>
    );
  }

  return (
    <Stack gap="sm">
      {!hideHeader && (
        <Group justify="space-between">
          <Text fw={500} size="sm">Map {providerLabel} fields to Hyperengage</Text>
          <Group>
            <DsButton
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={onRefreshFields}
              disabled={loading || !onRefreshFields}
              title="Refresh fields"
              aria-label="Refresh fields"
            >
              <IconRefresh size="0.75rem" className={loading ? "animate-spin" : undefined} />
            </DsButton>
          </Group>
        </Group>
      )}

      {hideHeader && onRefreshFields && (
        <Group justify="flex-end">
          <DsButton
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={onRefreshFields}
            disabled={loading}
            title="Refresh fields"
            aria-label="Refresh fields"
          >
            <IconRefresh size="0.75rem" className={loading ? "animate-spin" : undefined} />
          </DsButton>
        </Group>
      )}

      {!hideHeader && (
        <Text size="xs" c="dimmed">Map {providerLabel} fields to Hyperengage fields. Create new Hyperengage fields if needed.</Text>
      )}

      {(!hydrated && (loading || isFieldMappingsHydrationLoading)) ? (
        <Group justify="center" p="md">
          <Loader size="sm" />
          <Text size="xs">Loading field mappings...</Text>
        </Group>
      ) : (
        <div className="space-y-2">
          {(mappings.length > 0 || Boolean(matcherField?.hubspot && matcherField?.hyperengage)) && (
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_32px] items-center gap-3">
              <Text size="xs" fw={600} c="dimmed" className="uppercase tracking-wide">{providerLabel} Field</Text>
              <Text size="xs" fw={600} c="dimmed" className="uppercase tracking-wide">Hyperengage Column</Text>
              <span />
            </div>
          )}

          {/* Keep matcher visible as fixed row (system mappings are persisted but hidden). */}
          {matcherField?.hubspot && matcherField?.hyperengage && (
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_32px] items-center gap-3">
              <div className="h-10 rounded-[8px] border border-[var(--mantine-color-default-border)] bg-[var(--mantine-color-gray-0)] px-3 flex items-center">
                <Text size="sm" fw={500}>{matcherField.hubspot}</Text>
              </div>
              <div className="h-10 rounded-[8px] border border-[var(--mantine-color-default-border)] bg-[var(--mantine-color-gray-0)] px-3 flex items-center">
                {renderFieldLabelWithIcon(matcherField.hyperengage, matcherHyperengageFieldDefinition?.source)}
              </div>
              <div className="flex items-center justify-center">
                <Text size="xs" c="dimmed">Req</Text>
              </div>
            </div>
          )}

          {mappings.map((mapping, index) => {
            const selectableProviderFields = hubspotFields
              .filter((field) => {
                if (hubspotSystemFields.includes(field.key)) return false;
                const usedFields = getUsedHubSpotFields();
                const isCurrentSelection = field.key === mapping.source_field_name;
                const isLocallyReserved = usedFields.has(field.key) && !isCurrentSelection;
                const isGloballyReserved = reservedProviderFields.has(field.key) && !isCurrentSelection;
                return !isLocallyReserved && !isGloballyReserved;
              })
              .map((field) => ({
                value: field.key,
                label: `${field.title} (${getHubSpotFieldDisplayType(field)})`,
                searchText: `${field.key} ${field.type || ""}`,
              }));

            const selectedHubspotField = mapping.source_field_name
              ? hubspotFields.find((field) => field.key === mapping.source_field_name)
              : undefined;

            const selectableHyperengageFields = getCompatibleHyperengageFields(selectedHubspotField, mapping.hyperengage_field)
              .filter((field) => field && field.id)
              .map((field) => {
                const isMetric = field.attribute_type === "metric_traits";
                return {
                  value: field.id,
                  label: `${field.display_name || field.name}${isMetric ? " (Metric)" : ""}`,
                  searchText: `${field.name} ${field.data_type || ""}`,
                };
              });

            return (
              <div
                key={`mapping-${mapping.source_field_name || `new-${index}`}-${mapping.destination_field_id || 'unmapped'}`}
                className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_32px] items-center gap-3"
              >
                <SearchableRowSelect
                  value={mapping.source_field_name || ""}
                  options={[
                    { value: "", label: `Select ${providerLabel} field...`, searchText: "clear unselect" },
                    ...selectableProviderFields,
                  ]}
                  placeholder={`Select ${providerLabel} field...`}
                  onChange={(value) => {
                    const hubspotField = hubspotFields.find((field) => field.key === value);
                    handleFieldMappingChange(index, {
                      source_field_name: value,
                      hubspot_field: hubspotField,
                      mapping_status: "unmapped",
                    });
                  }}
                  className={mappingInputClass}
                />

                <SearchableRowSelect
                  value={mapping.destination_field_id || ""}
                  options={[
                    { value: "", label: "Select Hyperengage field...", searchText: "clear unselect" },
                    { value: "CREATE_FIELD", label: "+ Create New Field", searchText: "create new field" },
                    ...selectableHyperengageFields,
                  ]}
                  placeholder="Select Hyperengage field..."
                  onChange={(value) => {
                    if (value === "CREATE_FIELD") {
                      setModalHubSpotField(selectedHubspotField);
                      setCreatingFieldForMappingIndex(index);
                      openCreateModal();
                      return;
                    }

                    const selectedField = workspaceFields.find((field) => field.id === value);
                    const isMetric = selectedField?.attribute_type === "metric_traits";
                    handleFieldMappingChange(index, {
                      destination_field_id: value,
                      mapping_status: value ? "mapped" : "unmapped",
                      destination_field_type: isMetric ? "metric" : "field",
                      hyperengage_field: selectedField,
                    });
                  }}
                  className={mappingInputClass}
                />

                <button
                  type="button"
                  onClick={() => handleRemoveMapping(index)}
                  className="h-9 w-8 rounded flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-status-cancelled transition-colors"
                  aria-label="Remove mapping row"
                >
                  <IconTrash size="0.75rem" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="pt-2">
        <DsButton
          type="button"
          variant="outline"
          size="sm"
          className="h-8 text-[12px]"
          onClick={handleAddMapping}
        >
          <IconPlus size="0.75rem" className="mr-1.5" />
          Add a trait
        </DsButton>
      </div>

      {mappings.length === 0 && (
        <DsAlert className="border-[var(--mantine-color-default-border)] bg-[var(--mantine-color-gray-0)] px-4 py-3">
          <AlertDescription className="text-xs text-[var(--mantine-color-dimmed)]">
            No field mappings configured yet. Add mappings to sync data between {providerLabel} and Hyperengage.
          </AlertDescription>
        </DsAlert>
      )}

      {(mappings.some(m => m.mapping_status === 'mapped') || hasUnsavedChanges) && (
        persistenceMode === "component" ? (
          <Group justify="flex-end">
            <DsButton
              type="button"
              size="sm"
              className="h-8 text-[12px]"
              onClick={() => { saveFieldMappings().catch(console.error); }}
              disabled={!connectionId || savingMappings}
            >
              {savingMappings ? "Saving..." : "Save Mappings"}
            </DsButton>
          </Group>
        ) : null
      )}

      <CreateFieldModal
        opened={createModalOpened}
        onClose={() => {
          closeCreateModal();
          setModalHubSpotField(undefined);
          setCreatingFieldForMappingIndex(undefined);
        }}
        slug={slug}
        onSubmit={(field) => {
          handleCreateField(field).catch(console.error);
        }}
        hubspotField={modalHubSpotField}
        providerLabel={providerLabel}
      />
    </Stack>
  );
};

export default memo(FieldMappingTable);
